import ConstantMixin from "app/mixins/ConstantMixin.js"

import { type NetworkSlug } from "@/lib/networks"

/**
 * The bridge from the legacy `POST_SVC_*` / `INPUT_TYPE_*` / `INPUT_SUBTYPE_*`
 * service codes to a registry slug, so a `<Networks>` caller holding a backend
 * record can render a mark without knowing the numeric tables.
 *
 * It resolves through `ConstantMixin.getIconIndexByCode` rather than reading the
 * `window.DlvrConstants` codes directly: that switch is the app's single copy of
 * the code -> brand mapping, and a second copy here would need editing every
 * time a network is added. What this file owns is only the last hop, icon index
 * -> slug.
 *
 * Sibling of `networks.ts` rather than part of it because that file is generated
 * (`yarn networks`), and legacy service codes have no place in the manifest the
 * generator reads.
 */

/**
 * Icon index (`getIconIndexByCode`'s return) -> registry slug.
 *
 * Both members of an input/output icon pair land on the same slug: the registry
 * carries one entry per brand, and its `brand` colour is the OUTPUT one
 * (`instagram` #D6249F is `$instagramBusinessColor`, `wordpress` #21759B is
 * `$wordpressOutputColor`). So an output code renders exactly as it did before,
 * while an input code — `INPUT_TYPE_INSTAGRAM`, `INPUT_SUBTYPE_WORDPRESS` —
 * now draws the output brand colour instead of the muted input variant
 * ($instagramColor #d36ab4, $wordpressColor #87afc1). Every current caller is
 * output-side, so nothing regresses today; a surface that needs the input
 * treatment back needs a registry variant, not a second table here.
 *
 * Absent on purpose: `statusnet` (no artwork in either pipeline) and `generic`
 * (the switch's fallback, meaning "no brand mark"). Both resolve to null, which
 * callers already handle as "render unbadged".
 */
const SLUG_BY_ICON_INDEX: Readonly<Record<string, NetworkSlug>> = {
  blogger: "blogger",
  bluesky: "bluesky",
  discord: "discord",
  ebay: "ebay",
  etsy: "etsy",
  facebook: "facebook",
  feed: "rss",
  gmb: "google-business",
  instagram: "instagram",
  instagrambusiness: "instagram",
  linkedin: "linkedin",
  mastodon: "mastodon",
  nextdoor: "nextdoor",
  pinterest: "pinterest",
  reddit: "reddit",
  shopify: "shopify",
  slack: "slack",
  snapchat: "snapchat",
  stocktwits: "stocktwits",
  substack: "substack",
  threads: "threads",
  tiktok: "tiktok",
  tumblr: "tumblr",
  vimeo: "vimeo",
  wordpress: "wordpress",
  wordpressOutput: "wordpress",
  x: "x",
  youtube: "youtube",
}

/**
 * The code space a code belongs to. Omitted means the post/input space.
 *
 * These collide numerically — GENERIC_SVC_WORDPRESS and POST_SVC_TWITTER are
 * both 1 — so a code from another space read as a post code resolves to a
 * confidently wrong brand rather than to nothing. Only `generic` is offered:
 * `shortener` and `login` codes name a URL shortener and an identity provider,
 * neither of which is a network anything here would badge.
 */
export type ServiceCodeSpace = "generic"

/**
 * The network for a legacy service code, or null for a code with no mark in the
 * registry. Callers must handle null rather than assume a badge.
 *
 * Pass `space` for a code that is NOT a post or input code. A social record
 * carrying only `gsID` (a WordPress site posting through the generic connector)
 * is the case that needs it — read as a post code, GENERIC_SVC_WORDPRESS's 1 is
 * POST_SVC_TWITTER and the account draws the X mark.
 */
export function networkFromServiceCode(
  code: number | string | null | undefined,
  space?: ServiceCodeSpace
): NetworkSlug | null {
  if (code === null || code === undefined || code === "") return null
  return SLUG_BY_ICON_INDEX[ConstantMixin.getIconIndexByCode(code, space)] ?? null
}
